import type { Genre, Instrument, Mood, MusicalKey, SectionKind } from '../../domain/schemas';

/**
 * Genre profiles drive the procedural composer. Each profile changes the *music*
 * (tempo, groove, drum placement, harmony, instrumentation, sound design), not just a
 * label, so presets are audibly distinct.
 */
export interface DrumGrid {
  /** Base kick steps in a 16-step bar, plus optional extra hits added by intensity. */
  kick: number[];
  kickExtra: number[];
  /** Backbeat steps (snare and/or clap). */
  snare: number[];
  clap: number[];
  /** Hat spacing in steps (1 = 16ths, 2 = 8ths, 4 = quarters) and roll probability. */
  hatEvery: number;
  hatRolls: number;
  /** Percussion (conga/tom) steps; empty for none. */
  perc: number[];
}

export interface GenreProfile {
  label: string;
  description: string;
  bpm: number;
  bpmRange: [number, number];
  key: MusicalKey;
  mood: Mood;
  swing: number;
  drums: DrumGrid;
  /** Chord progressions as scale degrees (0 = tonic), one chord per bar. */
  progressions: number[][];
  sevenths: boolean;
  /** Instrument carrying the main melody, and how dense it is (onsets per bar at complexity 0.5). */
  melody: { instrument: Instrument; density: number; octave: number; style: 'arp' | 'stab' | 'phrase' };
  /** Secondary melodic voice, if any. */
  counter: { instrument: Instrument; density: number; octave: number } | null;
  pad: { style: 'sustain' | 'stab' | 'none' };
  bass: { style: 'syncopated' | 'long' | 'walking'; octave: number };
  /** Section template: kinds with relative weights. */
  form: [SectionKind, number][];
  /** Mixer adjustments relative to track defaults. */
  mix: Partial<Record<Instrument, { volumeDb?: number; drive?: number; cutoffHz?: number; delaySend?: number; reverbSend?: number; pan?: number }>>;
}

export const GENRES: Record<Genre, GenreProfile> = {
  'brazilian-phonk': {
    label: 'Brazilian phonk',
    description: 'Funk-style syncopated kicks, tamborzão percussion, distorted 808 and loud cowbell riffs.',
    bpm: 130,
    bpmRange: [120, 140],
    key: { root: 5, scale: 'minor' },
    mood: 'aggressive',
    swing: 0,
    drums: { kick: [0, 3, 6, 10], kickExtra: [12, 14], snare: [], clap: [4, 12], hatEvery: 4, hatRolls: 0, perc: [2, 5, 8, 11, 14] },
    progressions: [
      [0, 0, 5, 6],
      [0, 5, 3, 4],
      [0, 6, 5, 6],
    ],
    sevenths: false,
    melody: { instrument: 'cowbell', density: 7, octave: 5, style: 'stab' },
    counter: null,
    pad: { style: 'none' },
    bass: { style: 'syncopated', octave: 1 },
    form: [
      ['intro', 1],
      ['drop', 3],
      ['breakdown', 1],
      ['buildup', 1],
      ['drop', 3],
      ['outro', 1],
    ],
    mix: {
      kick: { drive: 0.35 },
      clap: { volumeDb: -3, reverbSend: 0.1 },
      bass808: { drive: 0.7, volumeDb: -5 },
      cowbell: { volumeDb: -5, drive: 0.25, cutoffHz: 12000, delaySend: 0.05 },
      perc: { volumeDb: -7, pan: -0.3 },
    },
  },
  'drift-phonk': {
    label: 'Drift phonk',
    description: 'Fast 16th-note cowbell arpeggios, long gliding distorted 808s and half-time snares.',
    bpm: 140,
    bpmRange: [125, 160],
    key: { root: 1, scale: 'minor' },
    mood: 'energetic',
    swing: 0,
    drums: { kick: [0, 10], kickExtra: [7, 13], snare: [8], clap: [8], hatEvery: 2, hatRolls: 0.25, perc: [] },
    progressions: [
      [0, 5, 6, 4],
      [0, 3, 5, 6],
      [0, 5, 2, 6],
    ],
    sevenths: false,
    melody: { instrument: 'cowbell', density: 14, octave: 5, style: 'arp' },
    counter: { instrument: 'pad', density: 0, octave: 4 },
    pad: { style: 'sustain' },
    bass: { style: 'long', octave: 1 },
    form: [
      ['intro', 1],
      ['verse', 2],
      ['buildup', 1],
      ['drop', 3],
      ['breakdown', 1],
      ['drop', 2],
      ['outro', 1],
    ],
    mix: {
      kick: { drive: 0.2 },
      bass808: { drive: 0.6, volumeDb: -4 },
      cowbell: { volumeDb: -6, cutoffHz: 14000, delaySend: 0.18, reverbSend: 0.2 },
      pad: { volumeDb: -18, reverbSend: 0.45 },
      hats: { volumeDb: -12 },
    },
  },
  'dark-phonk': {
    label: 'Dark phonk',
    description: 'Memphis-style slower groove, phrygian minor, eerie lead, deep reverb and sparse cowbell.',
    bpm: 130,
    bpmRange: [110, 150],
    key: { root: 2, scale: 'phrygian' },
    mood: 'dark',
    swing: 0.08,
    drums: { kick: [0, 7, 10], kickExtra: [3, 14], snare: [4, 12], clap: [], hatEvery: 2, hatRolls: 0.1, perc: [] },
    progressions: [
      [0, 1, 0, 5],
      [0, 5, 1, 0],
      [0, 3, 1, 0],
    ],
    sevenths: false,
    melody: { instrument: 'lead', density: 4, octave: 4, style: 'phrase' },
    counter: { instrument: 'cowbell', density: 3, octave: 5 },
    pad: { style: 'sustain' },
    bass: { style: 'long', octave: 1 },
    form: [
      ['intro', 2],
      ['verse', 2],
      ['buildup', 1],
      ['drop', 3],
      ['breakdown', 2],
      ['drop', 2],
      ['outro', 1],
    ],
    mix: {
      kick: { drive: 0.15 },
      snare: { reverbSend: 0.35 },
      bass808: { drive: 0.35, volumeDb: -4 },
      lead: { volumeDb: -10, cutoffHz: 3500, reverbSend: 0.5, delaySend: 0.3 },
      cowbell: { volumeDb: -12, reverbSend: 0.4, cutoffHz: 6000 },
      pad: { volumeDb: -13, cutoffHz: 1800, reverbSend: 0.6 },
    },
  },
  trap: {
    label: 'Trap',
    description: 'Half-time snares, rolling hi-hats with 32nd/triplet rolls, long 808s and a plucked lead.',
    bpm: 140,
    bpmRange: [130, 170],
    key: { root: 9, scale: 'minor' },
    mood: 'energetic',
    swing: 0,
    drums: { kick: [0, 10], kickExtra: [3, 7, 13], snare: [8], clap: [8], hatEvery: 2, hatRolls: 0.5, perc: [] },
    progressions: [
      [0, 5, 2, 6],
      [0, 3, 5, 4],
      [0, 6, 5, 3],
    ],
    sevenths: false,
    melody: { instrument: 'lead', density: 6, octave: 5, style: 'phrase' },
    counter: null,
    pad: { style: 'sustain' },
    bass: { style: 'long', octave: 1 },
    form: [
      ['intro', 1],
      ['verse', 3],
      ['buildup', 1],
      ['drop', 3],
      ['verse', 2],
      ['drop', 2],
      ['outro', 1],
    ],
    mix: {
      bass808: { drive: 0.15, volumeDb: -3 },
      hats: { volumeDb: -11, pan: 0.2 },
      lead: { volumeDb: -10, cutoffHz: 9000, delaySend: 0.25, reverbSend: 0.25 },
      pad: { volumeDb: -17 },
    },
  },
  lofi: {
    label: 'Lo-fi hip-hop',
    description: 'Slow swung boom-bap, jazzy seventh chords, soft clean bass and warm filtered tone.',
    bpm: 82,
    bpmRange: [70, 92],
    key: { root: 7, scale: 'dorian' },
    mood: 'chill',
    swing: 0.3,
    drums: { kick: [0, 7, 10], kickExtra: [14], snare: [4, 12], clap: [], hatEvery: 2, hatRolls: 0, perc: [] },
    progressions: [
      [1, 4, 0, 5],
      [0, 3, 6, 2],
      [3, 4, 2, 5],
    ],
    sevenths: true,
    melody: { instrument: 'lead', density: 4, octave: 5, style: 'phrase' },
    counter: null,
    pad: { style: 'sustain' },
    bass: { style: 'walking', octave: 2 },
    form: [
      ['intro', 1],
      ['verse', 4],
      ['breakdown', 1],
      ['verse', 3],
      ['outro', 1],
    ],
    mix: {
      kick: { volumeDb: -2, cutoffHz: 5000 },
      snare: { volumeDb: -6, cutoffHz: 6000, reverbSend: 0.2 },
      hats: { volumeDb: -15, cutoffHz: 7000 },
      bass808: { drive: 0, volumeDb: -6, cutoffHz: 900 },
      lead: { volumeDb: -12, cutoffHz: 2500, delaySend: 0.15, reverbSend: 0.35 },
      pad: { volumeDb: -12, cutoffHz: 2200, reverbSend: 0.35 },
    },
  },
};

export const GENRE_IDS = Object.keys(GENRES) as Genre[];
